import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Header from './Header';
import Pagination from './Pagination';
import {
  Users,
  Building,
  Landmark,
  Mail,
  Phone,
  Edit,
  CheckCircle,
  Send,
  Eye,
  FileText,
  Download,
  Search,
  Filter,
  User,
  ChevronDown,
  MoreVertical,
  AlertCircle,
  Shield,
  RefreshCw,
  X,
  Hash,
  Clock,
  DollarSign,
  TrendingUp,
  MapPin,
  FileCheck,
  MessageSquare
} from 'lucide-react';

const ModalPlotsCustomers = () => {
  const server_app_api_php = window.SERVER_APP_API_PHP;
  const business_locationIDMain = localStorage.getItem('business_locationIDMain');
  
  let userCompanyID = localStorage.getItem('companyID');
  if (userCompanyID === 'Admin') {
    userCompanyID = 1;
  }
  userCompanyID = JSON.parse(userCompanyID);

  const [businessLocationsPlots, setBusinessLocationsPlots] = useState([]);
  const [plotCustomers, setPlotCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editForm, setEditForm] = useState({
    lname: '',
    fname: '',
    phone: '',
    email: '',
    id: ''
  });

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  useEffect(() => {
    fetchBusinessLocations();
    fetchPlotCustomers();
  }, []);

  useEffect(() => {
    filterCustomers();
    setCurrentPage(1); // Reset to first page when filters change
  }, [searchQuery, selectedLocation, plotCustomers]);

  const fetchBusinessLocations = async () => {
    const formData = new FormData();
    formData.append("apiType", 'getBusinessLocationHousesOnUploadForm');
    formData.append("companyID", userCompanyID);
    
    try {
      const response = await axios.post(`${server_app_api_php}/server/index.php`, formData);
      setBusinessLocationsPlots(response.data);
    } catch (error) {
      console.error("Error fetching business locations:", error);
    }
  };

  const fetchPlotCustomers = async () => {
    setIsLoading(true);
    const formData = new FormData();
    formData.append('apiType', 'fetchPlotsCustomers');
    formData.append('companyID', userCompanyID);
    formData.append('business_locationIDMain', business_locationIDMain);

    try {
      const response = await axios.post(`${server_app_api_php}/server/index.php`, formData);
      const customersWithStatus = response.data.map(customer => ({
        ...customer,
        status: Math.random() > 0.7 ? 'active' : Math.random() > 0.5 ? 'pending' : 'inactive'
      }));
      setPlotCustomers(customersWithStatus);
      setFilteredCustomers(customersWithStatus);
    } catch (error) {
      console.error('Error fetching plot customers:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const filterCustomers = () => {
    let filtered = plotCustomers;

    if (searchQuery.trim() !== '') {
      filtered = filtered.filter(customer =>
        Object.values(customer).some(value =>
          value?.toString().toLowerCase().includes(searchQuery.toLowerCase())
        )
      );
    }

    if (selectedLocation) {
      filtered = filtered.filter(customer =>
        customer.businessLocationName === selectedLocation
      );
    }

    setFilteredCustomers(filtered);
  };

  // Calculate pagination
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = filteredCustomers.slice(indexOfFirstRow, indexOfLastRow);
  const totalPages = Math.ceil(filteredCustomers.length / rowsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleRowsPerPageChange = (newRowsPerPage) => {
    setRowsPerPage(newRowsPerPage);
    setCurrentPage(1);
  };

  const handleEditCustomer = (customer) => {
    setSelectedCustomer(customer);
    setEditForm({
      lname: customer.name.split(' ')[1] || '',
      fname: customer.name.split(' ')[0] || '',
      phone: customer.customersPhone || '',
      email: customer.customersEmail || '',
      id: customer.customersID || ''
    });
    setIsEditModalOpen(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    
    if (!editForm.lname || !editForm.fname || !editForm.phone || !editForm.email) {
      alert("All fields are required!");
      return;
    }

    const formData = new FormData();
    formData.append('email', editForm.email);
    formData.append('phone', editForm.phone);
    formData.append('lname', editForm.lname);
    formData.append('fname', editForm.fname);
    formData.append('customerID', editForm.id);
    formData.append('apiType', 'editPlotsCustomersDetails');

    try {
      const response = await axios.post(`${server_app_api_php}/server/index.php`, formData);
      alert(response.data.message || 'Customer details updated successfully!');
      setIsEditModalOpen(false);
      fetchPlotCustomers();
    } catch (error) {
      alert('Error updating customer details. Please try again.');
    }
  };

  const sendEmailPlotsLoginCredentials = async (id) => {
    const formData = new FormData();
    formData.append('apiType', 'sendEmailPlotsLoginCredentials');
    formData.append('id', id);
    
    try {
      const response = await axios.post(`${server_app_api_php}/server/index.php`, formData);
      alert('Login credentials sent successfully!');
    } catch (error) {
      alert('Error sending login credentials');
    }
  };

  const confirmCustomersAvailability = async (id) => {
    const formData = new FormData();
    formData.append('apiType', 'comfirmClientAvailability');
    formData.append('customerID', id);
    formData.append('customerType', 'Plots');
    
    try {
      const response = await axios.post(`${server_app_api_php}/server/payment_remindersEmailList.php`, formData);
      alert('Customer confirmed for payment reminders!');
    } catch (error) {
      alert('Error confirming customer');
    }
  };

  const sendPaymentReminder = async (customer) => {
    const formData = new FormData();
    formData.append('apiType', 'sendPaymentReminder');
    formData.append('email', customer.customersEmail);
    formData.append('customerName', customer.name);
    formData.append('plotNumber', customer.plotNumber);
    
    try {
      const response = await axios.post(`${server_app_api_php}/server/index.php`, formData);
      alert('Payment reminder sent successfully!');
    } catch (error) {
      alert('Error sending payment reminder');
    }
  };

  const generateCustomerStatement = async (customerId) => {
    try {
      const formData = new FormData();
      formData.append('apiType', 'generateCustomerStatementPDF');
      formData.append('customerId', customerId);
      
      const response = await axios.post(`${server_app_api_php}/server/index.php`, formData, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `customer-statement-${customerId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      alert('Error generating customer statement');
    }
  };

  const ActionMenu = ({ customer }) => (
    <div className="relative group">
      <button className="p-2 rounded-lg hover:bg-gray-100 transition-colors">
        <MoreVertical className="w-5 h-5 text-gray-600" />
      </button>
      <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-gray-200 z-50 invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-all duration-200">
        <div className="py-2">
          <button
            onClick={() => handleEditCustomer(customer)}
            className="w-full flex items-center space-x-3 px-4 py-2 text-gray-700 hover:bg-gray-50 transition-colors"
          >
            <Edit className="w-4 h-4" />
            <span>Edit Details</span>
          </button>
          <button
            onClick={() => sendEmailPlotsLoginCredentials(customer.customersID)}
            className="w-full flex items-center space-x-3 px-4 py-2 text-gray-700 hover:bg-gray-50 transition-colors"
          >
            <Mail className="w-4 h-4" />
            <span>Send Login Credentials</span>
          </button>
          <button
            onClick={() => confirmCustomersAvailability(customer.customersID)}
            className="w-full flex items-center space-x-3 px-4 py-2 text-gray-700 hover:bg-gray-50 transition-colors"
          >
            <CheckCircle className="w-4 h-4" />
            <span>Enable Payment Reminders</span>
          </button>
          <button
            onClick={() => sendPaymentReminder(customer)}
            className="w-full flex items-center space-x-3 px-4 py-2 text-gray-700 hover:bg-gray-50 transition-colors"
          >
            <MessageSquare className="w-4 h-4" />
            <span>Send Payment Reminder</span>
          </button>
          <button
            onClick={() => generateCustomerStatement(customer.customersID)}
            className="w-full flex items-center space-x-3 px-4 py-2 text-gray-700 hover:bg-gray-50 transition-colors"
          >
            <FileText className="w-4 h-4" />
            <span>Generate Statement</span>
          </button>
          <button
            onClick={() => window.open(`/ViewPlotsCustomers?customer=${customer.customersID}`, '_blank')}
            className="w-full flex items-center space-x-3 px-4 py-2 text-gray-700 hover:bg-gray-50 transition-colors"
          >
            <Eye className="w-4 h-4" />
            <span>View Full Statement</span>
          </button>
        </div>
      </div>
    </div>
  );

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { color: 'bg-green-100 text-green-800', icon: CheckCircle, label: 'Active' },
      pending: { color: 'bg-yellow-100 text-yellow-800', icon: Clock, label: 'Pending' },
      inactive: { color: 'bg-red-100 text-red-800', icon: AlertCircle, label: 'Inactive' }
    };
    
    const config = statusConfig[status] || statusConfig.inactive;
    const Icon = config.icon;
    
    return (
      <div className="flex items-center space-x-1">
        <Icon className="w-4 h-4" />
        <span className={`px-3 py-1 text-xs rounded-full ${config.color}`}>
          {config.label}
        </span>
      </div>
    );
  };

  const getUniqueLocations = () => {
    return [...new Set(filteredCustomers.map(c => c.businessLocationName))];
  };

  const getUniquePlots = () => {
    return [...new Set(filteredCustomers.map(c => c.plotNumber))];
  };

  const getActiveCustomersCount = () => {
    return filteredCustomers.filter(c => c.status === 'active').length;
  };

  const calculateAveragePlotsPerCustomer = () => {
    if (filteredCustomers.length === 0) return 0;
    return (getUniquePlots().length / filteredCustomers.length).toFixed(1);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-ZM', { style: 'currency', currency: 'ZMW' }).format(amount);
  };

  return (
    <>
      <Header />
      
      <main className="min-h-screen bg-gradient-to-b from-gray-50 to-white pt-16 lg:pl-64">
        <div className="p-6">
          <div className="max-w-7xl mx-auto">
            {/* Page Header */}
            <div className="mb-8">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-8">
                <div className="flex items-center space-x-4">
                  <div className="p-3 bg-gradient-to-br from-blue-500 to-purple-500 rounded-xl shadow-lg">
                    <Users className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <h1 className="text-3xl font-bold text-gray-900">Plot Customers</h1>
                    <p className="text-gray-600 mt-2">Manage and view all plot customers information</p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-3">
                  <button
                    onClick={fetchPlotCustomers}
                    className="flex items-center space-x-2 px-4 py-3 bg-gradient-to-r from-gray-800 to-gray-900 text-white rounded-xl hover:from-gray-900 hover:to-black transition-all duration-300 shadow-md hover:shadow-lg"
                  >
                    <RefreshCw className="w-4 h-4" />
                    <span>Refresh Data</span>
                  </button>
                </div>
              </div>

              {/* Stats Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Total Customers</p>
                      <p className="text-2xl font-bold text-gray-900 mt-2">{filteredCustomers.length}</p>
                      <div className="flex items-center space-x-2 mt-2">
                        <Hash className="w-4 h-4 text-green-500" />
                        <span className="text-xs text-green-600">{getActiveCustomersCount()} active</span>
                      </div>
                    </div>
                    <div className="p-3 bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl">
                      <Users className="w-8 h-8 text-blue-500" />
                    </div>
                  </div>
                </div>
                
                <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Active Locations</p>
                      <p className="text-2xl font-bold text-gray-900 mt-2">{getUniqueLocations().length}</p>
                      <div className="flex items-center space-x-2 mt-2">
                        <MapPin className="w-4 h-4 text-purple-500" />
                        <span className="text-xs text-purple-600">{selectedLocation || 'All locations'}</span>
                      </div>
                    </div>
                    <div className="p-3 bg-gradient-to-br from-green-50 to-green-100 rounded-xl">
                      <Building className="w-8 h-8 text-green-500" />
                    </div>
                  </div>
                </div>
                
                <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Total Plots</p>
                      <p className="text-2xl font-bold text-gray-900 mt-2">{getUniquePlots().length}</p>
                      <div className="flex items-center space-x-2 mt-2">
                        <Landmark className="w-4 h-4 text-orange-500" />
                        <span className="text-xs text-orange-600">
                          {calculateAveragePlotsPerCustomer()} plots/customer
                        </span>
                      </div>
                    </div>
                    <div className="p-3 bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl">
                      <Landmark className="w-8 h-8 text-purple-500" />
                    </div>
                  </div>
                </div>
                
                <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Status Overview</p>
                      <div className="mt-2 space-y-1">
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-green-600">Active</span>
                          <span className="text-sm font-bold">{getActiveCustomersCount()}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-yellow-600">Pending</span>
                          <span className="text-sm font-bold">
                            {filteredCustomers.filter(c => c.status === 'pending').length}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="p-3 bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl">
                      <TrendingUp className="w-8 h-8 text-orange-500" />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Filters and Search */}
            <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100 mb-8">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                <div className="flex-1">
                  <div className="relative">
                    <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      type="text"
                      placeholder="Search by name, plot number, email, phone..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-12 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent shadow-sm"
                    />
                    {searchQuery && (
                      <button
                        onClick={() => setSearchQuery('')}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    )}
                  </div>
                </div>
                
                <div className="flex flex-col md:flex-row gap-4">
                  <div className="relative">
                    <Building className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <select
                      value={selectedLocation}
                      onChange={(e) => setSelectedLocation(e.target.value)}
                      className="pl-10 pr-8 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent shadow-sm appearance-none"
                    >
                      <option value="">All Locations</option>
                      {businessLocationsPlots.map((location, index) => (
                        <option key={index} value={location.businessLocationName}>
                          {location.businessLocationName}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  </div>
                  
                  <div className="flex items-center space-x-3">
                    <Filter className="w-5 h-5 text-gray-400" />
                    <span className="text-sm text-gray-600">
                      {filteredCustomers.length} customers found
                      {searchQuery || selectedLocation ? ` (filtered)` : ''}
                    </span>
                  </div>
                </div>
              </div>
              
              {/* Active Filters */}
              {(searchQuery || selectedLocation) && (
                <div className="mt-4 flex flex-wrap gap-2">
                  {searchQuery && (
                    <div className="flex items-center space-x-1 px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
                      <span>Search: "{searchQuery}"</span>
                      <button onClick={() => setSearchQuery('')}>
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                  {selectedLocation && (
                    <div className="flex items-center space-x-1 px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm">
                      <span>Location: {selectedLocation}</span>
                      <button onClick={() => setSelectedLocation('')}>
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Customers Table */}
            <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100">
                <div className="flex flex-col md:flex-row md:items-center justify-between">
                  <div className="flex items-center space-x-3 mb-4 md:mb-0">
                    <FileCheck className="w-5 h-5 text-gray-600" />
                    <h2 className="text-lg font-semibold text-gray-900">Customers List</h2>
                    <span className="px-3 py-1 bg-gray-200 text-gray-800 rounded-full text-sm">
                      Showing {Math.min(rowsPerPage, currentRows.length)} of {filteredCustomers.length} customers
                    </span>
                  </div>
                  
                  <div className="flex items-center space-x-4">
                    <select
                      value={rowsPerPage}
                      onChange={(e) => handleRowsPerPageChange(Number(e.target.value))}
                      className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="5">5 per page</option>
                      <option value="10">10 per page</option>
                      <option value="25">25 per page</option>
                      <option value="50">50 per page</option>
                    </select>
                  </div>
                </div>
              </div>

              {isLoading ? (
                <div className="p-16 text-center">
                  <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
                  <p className="mt-4 text-gray-600">Loading customers data...</p>
                  <p className="text-sm text-gray-500 mt-2">Please wait while we fetch the latest information</p>
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr className="border-b border-gray-200">
                          <th className="py-4 px-6 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                            <div className="flex items-center">
                              <Hash className="w-4 h-4 mr-2" />
                              SN
                            </div>
                          </th>
                          <th className="py-4 px-6 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                            <div className="flex items-center">
                              <Building className="w-4 h-4 mr-2" />
                              Location
                            </div>
                          </th>
                          <th className="py-4 px-6 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                            <div className="flex items-center">
                              <Landmark className="w-4 h-4 mr-2" />
                              Plot Numbers
                            </div>
                          </th>
                          <th className="py-4 px-6 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                            <div className="flex items-center">
                              <User className="w-4 h-4 mr-2" />
                              Customer Details
                            </div>
                          </th>
                          <th className="py-4 px-6 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                            <div className="flex items-center">
                              <Phone className="w-4 h-4 mr-2" />
                              Contact Info
                            </div>
                          </th>
                          <th className="py-4 px-6 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                            <div className="flex items-center">
                              <Clock className="w-4 h-4 mr-2" />
                              Status
                            </div>
                          </th>
                          <th className="py-4 px-6 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                            <div className="flex items-center">
                              <MoreVertical className="w-4 h-4 mr-2" />
                              Actions
                            </div>
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {currentRows.map((customer, index) => (
                          <tr 
                            key={customer.customersID || index} 
                            className="hover:bg-gray-50 transition-colors duration-200"
                          >
                            <td className="py-4 px-6">
                              <div className="flex items-center">
                                <div className="w-8 h-8 flex items-center justify-center bg-gray-100 rounded-lg">
                                  <span className="text-sm font-medium text-gray-700">
                                    {indexOfFirstRow + index + 1}
                                  </span>
                                </div>
                              </div>
                            </td>
                            <td className="py-4 px-6">
                              <div className="flex items-center">
                                <Building className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" />
                                <span className="text-sm text-gray-900 truncate max-w-xs">
                                  {customer.businessLocationName}
                                </span>
                              </div>
                            </td>
                            <td className="py-4 px-6">
                              <div className="space-y-1">
                                <div className="flex items-center">
                                  <Landmark className="w-4 h-4 text-blue-400 mr-2" />
                                  <span className="text-sm font-medium text-blue-600">
                                    {customer.plotNumber}
                                  </span>
                                </div>
                                {customer.plotNumbers && customer.plotNumbers !== customer.plotNumber && (
                                  <div className="flex items-center ml-6">
                                    <span className="text-xs text-gray-500">
                                      New: {customer.plotNumbers}
                                    </span>
                                  </div>
                                )}
                              </div>
                            </td>
                            <td className="py-4 px-6">
                              <div className="flex items-center">
                                <div className="w-10 h-10 bg-gradient-to-br from-blue-100 to-blue-200 rounded-lg flex items-center justify-center mr-3">
                                  <User className="w-5 h-5 text-blue-600" />
                                </div>
                                <div className="min-w-0">
                                  <p className="text-sm font-medium text-gray-900 truncate">
                                    {customer.name}
                                  </p>
                                  <p className="text-xs text-gray-500 truncate">
                                    ID: {customer.customersID}
                                  </p>
                                </div>
                              </div>
                            </td>
                            <td className="py-4 px-6">
                              <div className="space-y-2">
                                <div className="flex items-center">
                                  <Mail className="w-3 h-3 text-gray-400 mr-2 flex-shrink-0" />
                                  <span className="text-xs text-gray-600 truncate max-w-xs">
                                    {customer.customersEmail}
                                  </span>
                                </div>
                                <div className="flex items-center">
                                  <Phone className="w-3 h-3 text-gray-400 mr-2 flex-shrink-0" />
                                  <span className="text-xs text-gray-600">
                                    {customer.customersPhone || 'Not provided'}
                                  </span>
                                </div>
                              </div>
                            </td>
                            <td className="py-4 px-6">
                              {getStatusBadge(customer.status)}
                            </td>
                            <td className="py-4 px-6">
                              <ActionMenu customer={customer} />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleRowsPerPageChange}
                    totalItems={filteredCustomers.length}
                  />
                </>
              )}

              {filteredCustomers.length === 0 && !isLoading && (
                <div className="p-16 text-center">
                  <div className="mx-auto w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                    <Users className="w-10 h-10 text-gray-400" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No customers found</h3>
                  <p className="text-gray-600 max-w-md mx-auto">
                    {searchQuery || selectedLocation 
                      ? `No customers match your search criteria. Try adjusting your filters.`
                      : 'No customers available. Customers will appear here once added.'}
                  </p>
                  {(searchQuery || selectedLocation) && (
                    <button
                      onClick={() => {
                        setSearchQuery('');
                        setSelectedLocation('');
                      }}
                      className="mt-4 px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all"
                    >
                      Clear all filters
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Edit Customer Modal */}
      {isEditModalOpen && selectedCustomer && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-gradient-to-br from-blue-500 to-purple-500 rounded-xl">
                  <Edit className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">Edit Customer</h3>
                  <p className="text-sm text-gray-600">Update customer details</p>
                </div>
              </div>
              <button
                onClick={() => setIsEditModalOpen(false)}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6">
              {/* Current Info */}
              <div className="mb-6 bg-gradient-to-br from-gray-50 to-gray-100 p-4 rounded-xl border border-gray-200">
                <h4 className="font-medium text-gray-900 mb-3 flex items-center">
                  <FileCheck className="w-4 h-4 mr-2" />
                  Current Information
                </h4>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-xs text-gray-600">Name</p>
                    <p className="text-sm font-medium">{selectedCustomer.name}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600">Phone</p>
                    <p className="text-sm font-medium">{selectedCustomer.customersPhone || 'N/A'}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-xs text-gray-600">Email</p>
                    <p className="text-sm font-medium">{selectedCustomer.customersEmail}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600">Plot</p>
                    <p className="text-sm font-medium">{selectedCustomer.plotNumber}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600">Location</p>
                    <p className="text-sm font-medium">{selectedCustomer.businessLocationName}</p>
                  </div>
                </div>
              </div>

              {/* Edit Form */}
              <form onSubmit={handleEditSubmit} className="space-y-5">
                <div>
                  <label htmlFor="fname" className="block text-sm font-medium text-gray-700 mb-2">
                    <User className="w-4 h-4 inline mr-1" />
                    First Name *
                  </label>
                  <input
                    type="text"
                    id="fname"
                    value={editForm.fname}
                    onChange={(e) => setEditForm({...editForm, fname: e.target.value})}
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    placeholder="Enter first name"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="lname" className="block text-sm font-medium text-gray-700 mb-2">
                    <User className="w-4 h-4 inline mr-1" />
                    Last Name *
                  </label>
                  <input
                    type="text"
                    id="lname"
                    value={editForm.lname}
                    onChange={(e) => setEditForm({...editForm, lname: e.target.value})}
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    placeholder="Enter last name"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                    <Phone className="w-4 h-4 inline mr-1" />
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    value={editForm.phone}
                    onChange={(e) => setEditForm({...editForm, phone: e.target.value})}
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    placeholder="Enter phone number"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    <Mail className="w-4 h-4 inline mr-1" />
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    value={editForm.email}
                    onChange={(e) => setEditForm({...editForm, email: e.target.value})}
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    placeholder="Enter email address"
                    required
                  />
                </div>

                <div className="pt-2">
                  <button
                    type="submit"
                    className="w-full py-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-xl hover:from-blue-600 hover:to-purple-600 transition-all duration-300 font-medium shadow-md hover:shadow-lg"
                  >
                    <CheckCircle className="w-5 h-5 inline mr-2" />
                    Update Customer Details
                  </button>
                </div>
              </form>
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-2xl">
              <p className="text-xs text-gray-500">
                <Shield className="w-3 h-3 inline mr-1" />
                Fields marked with * are required. Changes will be reflected immediately after saving.
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ModalPlotsCustomers;